/**
 * Wer ist angemeldet, und darf diese Person überhaupt hier sein?
 *
 * Zwei getrennte Fragen, und sie werden bewusst getrennt beantwortet:
 *
 * 1. Supabase Auth sagt, wem die E-Mail-Adresse gehört. Das ist die Identität.
 * 2. Die Tabelle `mitarbeiter` sagt, welche Rolle diese Identität im Betrieb
 *    hat. Das ist die Befugnis.
 *
 * Ein Fahrerkonto kann sich hier anmelden — es hat ja gültige Zugangsdaten —
 * und sieht dann trotzdem nichts. Das ist kein Fehler, sondern der Entwurf:
 * Die Oberfläche prüft die Rolle, damit niemand vor leeren Listen sitzt und
 * rätselt; die eigentliche Sperre liegt in der Datenbank (`ist_buero()`), wo
 * sie nicht umgangen werden kann. Die Prüfung hier ist Höflichkeit, nicht
 * Sicherheit — das ist ein wichtiger Unterschied, wenn später jemand auf die
 * Idee kommt, sie „zur Vereinfachung" wegzulassen.
 */

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '../supabase';
import { alsKlarfehler, type Klarfehler } from '../daten/fehler';
import { STATUSTEXT, type Status } from '../daten/mitarbeiter';
import { alleEntwuerfeLoeschen } from '../daten/entwurf';

export type Rolle = 'fahrer' | 'buero' | 'admin';

export type AngemeldeterMitarbeiter = {
  id: string;
  vorname: string;
  nachname: string;
  rolle: Rolle;
  status: string;
};

type Sitzungsstand =
  /** Wir wissen es noch nicht — beim ersten Laden der Seite. */
  | { art: 'prueft' }
  /** Niemand angemeldet. */
  | { art: 'abgemeldet' }
  /** Angemeldet, aber ohne Bürorolle — oder ohne Mitarbeiterdatensatz. */
  | { art: 'kein_zugang'; grund: string; email: string | null }
  /**
   * Angemeldet und befugt.
   *
   * `passwortWechseln` kommt aus den Angaben am Konto und wird gesetzt, wenn
   * das Passwort von jemand anderem vergeben wurde — beim Anlegen des Zugangs
   * oder beim Zurücksetzen. Solange es steht, lässt das Backoffice nur den
   * Wechsel zu. Ein Einmalpasswort, das jemand am Telefon durchgegeben hat,
   * darf nicht das Passwort bleiben, mit dem monatelang gearbeitet wird.
   */
  | {
      art: 'bereit';
      mitarbeiter: AngemeldeterMitarbeiter;
      email: string | null;
      passwortWechseln: boolean;
    }
  /** Die Rollenabfrage selbst ist gescheitert (meist: kein Netz). */
  | { art: 'gestoert'; fehler: Klarfehler };

type Sitzungswerkzeug = {
  stand: Sitzungsstand;
  anmelden: (email: string, passwort: string) => Promise<void>;
  abmelden: () => Promise<void>;
  nochmalPruefen: () => void;
};

const SitzungKontext = createContext<Sitzungswerkzeug | null>(null);

/**
 * Die Befugnis zu einem Konto nachschlagen. Eine Funktion statt Code im
 * Effekt, weil sie an zwei Stellen gebraucht wird: laut beim Anmelden und
 * still bei jeder Meldung von Supabase (Schritt 3).
 */
async function befugnisPruefen(
  kontoId: string,
  email: string | null,
  passwortWechseln: boolean,
): Promise<Sitzungsstand> {
  const { data, error } = await supabase
    .from('mitarbeiter')
    .select('id, vorname, nachname, rolle, status')
    .eq('auth_user_id', kontoId)
    .maybeSingle();

  if (error) {
    return { art: 'gestoert', fehler: alsKlarfehler(error) };
  }

  if (!data) {
    return {
      art: 'kein_zugang',
      email,
      grund:
        'Zu diesem Zugang gibt es keinen Mitarbeiterdatensatz. Das Konto muss im Backoffice mit einem Mitarbeiter verknüpft werden.',
    };
  }

  const mitarbeiter = data as AngemeldeterMitarbeiter;

  if (mitarbeiter.status !== 'aktiv') {
    return {
      art: 'kein_zugang',
      email,
      grund: `Der Mitarbeiterdatensatz steht auf „${STATUSTEXT[mitarbeiter.status as Status] ?? mitarbeiter.status}". Nur aktive Mitarbeiter können sich anmelden.`,
    };
  }

  if (mitarbeiter.rolle !== 'buero' && mitarbeiter.rolle !== 'admin') {
    return {
      art: 'kein_zugang',
      email,
      grund:
        'Dieser Zugang gehört zu einem Fahrerkonto. Das Backoffice ist für Büro und Verwaltung. Die Fahrer-App meldest du mit dem Einrichtungscode an, nicht mit E-Mail und Passwort.',
    };
  }

  return { art: 'bereit', mitarbeiter, email, passwortWechseln };
}

/**
 * Sagen zwei Stände für die Oberfläche dasselbe? Dann bleibt der alte —
 * ein neues, gleiches Objekt würde jeden Bildschirm neu zeichnen lassen.
 */
function gleicherStand(a: Sitzungsstand, b: Sitzungsstand): boolean {
  if (a.art !== b.art) return false;
  if (a.art === 'bereit' && b.art === 'bereit') {
    return (
      a.mitarbeiter.id === b.mitarbeiter.id &&
      a.mitarbeiter.rolle === b.mitarbeiter.rolle &&
      a.mitarbeiter.status === b.mitarbeiter.status &&
      a.mitarbeiter.vorname === b.mitarbeiter.vorname &&
      a.mitarbeiter.nachname === b.mitarbeiter.nachname &&
      a.email === b.email &&
      a.passwortWechseln === b.passwortWechseln
    );
  }
  if (a.art === 'kein_zugang' && b.art === 'kein_zugang') {
    return a.grund === b.grund && a.email === b.email;
  }
  return true;
}

export function SitzungAnbieter(eigenschaften: { children: ReactNode }) {
  const [sitzung, setSitzung] = useState<Session | null>(null);
  const [sitzungGeklaert, setSitzungGeklaert] = useState(false);
  const [stand, setStand] = useState<Sitzungsstand>({ art: 'prueft' });
  const [versuch, setVersuch] = useState(0);
  /** Zählt jede Meldung von Supabase — Anlass für die stille Nachprüfung. */
  const [meldungen, setMeldungen] = useState(0);

  // Schritt 1: Was sagt Supabase über die gespeicherte Sitzung?
  useEffect(() => {
    let lebt = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!lebt) return;
      setSitzung(data.session);
      setSitzungGeklaert(true);
    });

    const { data: horcher } = supabase.auth.onAuthStateChange((_, neue) => {
      // Hier bewusst NUR Zustand setzen. Ein Supabase-Aufruf direkt in diesem
      // Rückruf kann sich mit dem internen Schloss des Clients verhaken; das
      // Nachladen der Rolle passiert deshalb unten im zweiten Effekt.
      setSitzung(neue);
      setSitzungGeklaert(true);
      setMeldungen((n) => n + 1);
    });

    return () => {
      lebt = false;
      horcher.subscription.unsubscribe();
    };
  }, []);

  // ═══ NEU GEPRÜFT WIRD NUR BEI EINEM ANDEREN KONTO (HJ-025) ═══
  //
  // Supabase meldet dieselbe Sitzung immer wieder als NEUES Objekt: beim
  // Zurückkommen in den Browser-Tab (SIGNED_IN) und beim stündlichen
  // Erneuern des Schlüssels (TOKEN_REFRESHED). Hing die Rollenprüfung am
  // Objekt, lief sie jedes Mal neu — mit „Anmeldung wird geprüft" an Stelle
  // der ganzen Oberfläche. Damit war jedes offene Formular zu und jede
  // Eingabe ohne Entwurf weg; im Testlauf sah das aus wie „das Formular
  // schließt sich". Geprüft wird deshalb an dem, was die Antwort ändern kann:
  // welches Konto, welche Adresse, ob ein Passwortwechsel ansteht.
  //
  // Eine Sperre wirkt trotzdem sofort — dort, wo sie sitzt: in der
  // Zeilensicherheit der Datenbank. Die Prüfung hier ist Höflichkeit.
  const kontoId = sitzung?.user.id ?? null;
  const kontoEmail = sitzung?.user.email ?? null;
  const passwortWechseln = sitzung?.user.user_metadata?.passwort_wechseln === true;

  // ═══ BEIM ABMELDEN UND KONTOWECHSEL: ENTWÜRFE WEG ═══
  //
  // Seit HJ-025 merken sich Anlegeformulare ihre Eingaben im Tab. Die
  // gehören der Person, die sie getippt hat — die nächste am selben Büro-PC
  // darf sie nicht offen und gefüllt vorfinden. Am Konto und nicht am
  // Abmelde-Knopf, damit auch eine abgelaufene Sitzung aufräumt.
  const vorigesKonto = useRef<string | null>(null);
  useEffect(() => {
    if (!sitzungGeklaert) return;
    if (vorigesKonto.current !== null && vorigesKonto.current !== kontoId) {
      alleEntwuerfeLoeschen();
    }
    vorigesKonto.current = kontoId;
  }, [kontoId, sitzungGeklaert]);

  // Schritt 2: Rolle aus dem Betrieb nachschlagen — mit „wird geprüft".
  useEffect(() => {
    if (!sitzungGeklaert) return;

    if (!kontoId) {
      setStand({ art: 'abgemeldet' });
      return;
    }

    let lebt = true;
    setStand({ art: 'prueft' });
    void befugnisPruefen(kontoId, kontoEmail, passwortWechseln).then((neu) => {
      if (lebt) setStand(neu);
    });

    return () => {
      lebt = false;
    };
  }, [kontoId, kontoEmail, passwortWechseln, sitzungGeklaert, versuch]);

  // ═══ SCHRITT 3: STILL NACHPRÜFEN BEI JEDER MELDUNG VON SUPABASE ═══
  //
  // Eine Sperre oder Herabstufung soll weiter spätestens beim nächsten
  // Tabwechsel oder nach einer Stunde auch in der OBERFLÄCHE ankommen, wie
  // vor HJ-025 — aber ohne die Oberfläche dafür abzubauen. Also im
  // Hintergrund nachsehen und den Stand nur ändern, wenn die Antwort etwas
  // anderes sagt. Scheitert die stille Abfrage (kein Netz), bleibt alles,
  // wie es ist: Ein Funkloch ist kein Grund, das Formular zu schließen.
  // Stand „gestört" dagegen nimmt jede neue Antwort an — das ist der
  // automatische zweite Versuch, den es vorher auch gab.
  useEffect(() => {
    if (meldungen === 0 || !kontoId) return;
    let lebt = true;
    void befugnisPruefen(kontoId, kontoEmail, passwortWechseln).then((neu) => {
      if (!lebt) return;
      setStand((bisher) => {
        if (bisher.art === 'prueft') return bisher;
        if (neu.art === 'gestoert') return bisher;
        return gleicherStand(bisher, neu) ? bisher : neu;
      });
    });
    return () => {
      lebt = false;
    };
    // Bewusst nur an `meldungen`: Konto, Adresse und Passwortwechsel haben
    // ihren eigenen, lauten Weg in Schritt 2.
  }, [meldungen]);

  const werkzeug: Sitzungswerkzeug = {
    stand,
    anmelden: async (email, passwort) => {
      const { error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password: passwort,
      });
      if (error) throw error;
    },
    abmelden: async () => {
      await supabase.auth.signOut();
    },
    nochmalPruefen: () => setVersuch((n) => n + 1),
  };

  return (
    <SitzungKontext.Provider value={werkzeug}>
      {eigenschaften.children}
    </SitzungKontext.Provider>
  );
}

export function useSitzung(): Sitzungswerkzeug {
  const werkzeug = useContext(SitzungKontext);
  if (!werkzeug) {
    throw new Error('useSitzung ausserhalb von SitzungAnbieter verwendet.');
  }
  return werkzeug;
}

/** Kurzform für „darf diese Person Stammdaten ändern". */
export function useMitarbeiter(): AngemeldeterMitarbeiter {
  const { stand } = useSitzung();
  if (stand.art !== 'bereit') {
    throw new Error('useMitarbeiter nur innerhalb des angemeldeten Bereichs.');
  }
  return stand.mitarbeiter;
}
